import { CheckCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Button, Modal } from 'antd';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { getFragmentData } from '../../../graphql/generated';
import { useCurrentContextStore } from '../../../hooks/store/useCurrentContextStore';
import { useNewsConfirmationStore } from '../../../hooks/store/useNewsConfirmationStore.ts';
import type { UpdateEvent } from '../../../socket/types.ts';
import { useSubscription } from '../../../socket/useSubscription.ts';
import { NEWS_CONFIRMATION_MODAL_QUERY } from './NewsConfirmationModal.gql';
import { NewsListFragment } from './NewsList.gql';
import { NewsList } from './NewsList.tsx';
export const NewsConfirmationModal: React.FC = () => {
  const { currentDoctorId } = useCurrentContextStore();
  const { confirmedIds, setConfirmedIds } = useNewsConfirmationStore();

  const { data, refetch } = useQuery(NEWS_CONFIRMATION_MODAL_QUERY, {
    variables: {
      doctorId: currentDoctorId,
    },
    skip: !currentDoctorId,
  });

  const callback = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received news update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );

  useSubscription({ type: 'news-updated' }, callback);

  const newsData = useMemo(() => {
    const items = getFragmentData(NewsListFragment, data?.relevantNews ?? []);
    const unconfirmedFragments =
      data?.relevantNews?.filter(it => !confirmedIds.includes(getFragmentData(NewsListFragment, it).confirmationId)) ?? [];
    const unconfiremdItems = items.filter(it => !confirmedIds.includes(it.confirmationId));
    return {
      unconfirmedFragments,
      unconfiremdItems,
    };
  }, [confirmedIds, data]);

  return (
    <Modal
      title="Aktuelle Informationen und Ankündigungen"
      open={!!newsData.unconfiremdItems.length}
      width="80%"
      closable={false}
      keyboard={false}
      maskClosable={false}
      className={css`
        top: 80px;
        padding-bottom: 0;
        max-width: 1400px;

        .ant-modal-body {
          max-height: calc(100vh - 280px);
          overflow: auto;
          padding: 0;
        }
      `}
      destroyOnHidden
      footer={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() =>
            setConfirmedIds(
              [...newsData.unconfiremdItems.map(it => it.confirmationId), ...confirmedIds].slice(0, 300) // max. 300 IDs in localStorage
            )
          }
        >
          Ich habe die Mitteilungen gelesen
        </Button>
      }
    >
      <NewsList newsFragments={newsData.unconfirmedFragments} mode="modal" />
    </Modal>
  );
};
